import { InlineEditorCancelButton, InlineEditorEditButton,InlineEditorLogic, InlineEditorLogic_EditMode, InlineEditorSaveButton, isReadonlyMode, useInlineEditor } from '@tableau/vizportalclient-common-components';
import { TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '~common/react/CommonStyles';
import { SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { Formatters,Messages } from '../../codegen/Localize';
import { CollectionsErrorCode } from '../../ts/helpers/ErrorCodes';
import { CollectionsResourceQuery } from '../ResourceQuery/CollectionsResourceQuery';
import { UpdateCollectionsResponseData } from './CollectionModels';
import { CollectionSpaceHeaderProps } from './CollectionSpaceHeader';

export const COLLECTION_NAME_MAX_LENGTH = 80; // if this max changes, update `createCollectionNameTooLongError` string

export interface EditableCollectionNameComponentProps extends CollectionSpaceHeaderProps {
  readonly onEditChange: (isEditing: boolean) => void;
  readonly canEdit: boolean;
}

export function EditableCollectionName(props: EditableCollectionNameComponentProps) {
  const [errorMessage, setErrorMessage] = React.useState(undefined);
  const [name, setName] = React.useState(props.item.name);

  const updateErrorMessage = (newMessage: string): void => {
    setErrorMessage(newMessage);
  };

  const clearErrorMessage = (): void => {
    setErrorMessage(undefined);
  };

  const saveNameEdit = (luid: string, updateErrorMessage: (newMessage: string) => void) => {
    return async (newName: string): Promise<void> => {
      const updatedCollectionData = {
        luid,
        name: newName
      };

      try {
        const response: UpdateCollectionsResponseData = await (new CollectionsResourceQuery()).update([updatedCollectionData]);

        if (response.errors && response.errors.length > 0) {
          throw { // rethrow as error to go into catch which handles all error cases
            data: {
              errorCode: response.errors[0].errorCode
            }
          };
        }
        props.reloadSpace();
        props.onEditChange && props.onEditChange(false);
        setName(newName);
        updateErrorMessage(undefined);
      } catch (e) {
        let errorText = Messages.collectionRenameError({
          COLLECTION_NAME: newName
        });
        if (e.data && e.data.errorCode === CollectionsErrorCode.DUPLICATE_KEY) {
          errorText = props.isOwner ? Messages.collectionOwnerDuplicateRenameError() : Messages.collectionNonOwnerDuplicateRenameError();
        }

        updateErrorMessage(errorText);
        throw e;
      }
    };
  };

  const canEdit = props.isAdminOrOwner && props.canEdit;

  const logic: InlineEditorLogic<string> = useInlineEditor<string>({
    editorStateWhenEnteringEditMode: name,
    canEdit: canEdit,
    onSave: saveNameEdit(props.item.luid, updateErrorMessage),
    onCancel: () => {
      props.onEditChange && props.onEditChange(false);
      setErrorMessage(undefined);
    },
    canSaveFn: isValidAndChangedName(name),
  });

  const editableStyling: string = canEdit ? SpaceHeaderStyles.HeaderEditableInlineElement : '';

  if (isReadonlyMode(logic)) {
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return <h1
      data-tb-test-id={SpaceHeaderTestIds.name}
      className={`${SpaceHeaderStyles.EditableTitleReadonly} ${editableStyling}`}
      onClick={() => {
        canEdit && logic.enterEditMode();
        props.onEditChange && props.onEditChange(true);
      }}
    >
      {name}
      {props.isAdminOrOwner &&
        <span style={{ paddingLeft: CommonStyles.Sizes.SmallSpace }}>
          <InlineEditorEditButton {...logic} editButtonLabel={Messages.editName()} />
        </span>
      }
    </h1>;
  } else {
    return <CollectionInlineNameEditorEditMode editLogic={logic} errorMessage={errorMessage} clearErrorMessage={clearErrorMessage}/>;
  }
}

function isValidAndChangedName(originalName: string): (name: string) => boolean {
  return (name: string) => (!_.isUndefined(name) &&
    name.trim().length > 0 &&
    name.length <= COLLECTION_NAME_MAX_LENGTH &&
    name !== originalName);  // should only allow saving if name is valid and different
}

export interface CollectionInlineNameEdtitorEditModeProps {
  readonly editLogic: InlineEditorLogic_EditMode<string>;
  readonly errorMessage: string;
  readonly clearErrorMessage: () => void;
}

export function CollectionInlineNameEditorEditMode(props: CollectionInlineNameEdtitorEditModeProps): JSX.Element {
  const inputEl = React.useRef<HTMLInputElement>(null);
  const [isErrored, setIsErrored] = React.useState(undefined);

  const getMessageForNameTextInput = (value: string): string => {
    if (value.trim().length === 0) {
      !isErrored && setIsErrored(true);
      return Messages.createCollectionNameWhitespaceError();
    } else if (value.length > COLLECTION_NAME_MAX_LENGTH) {
      !isErrored && setIsErrored(true);
      return Messages.createCollectionNameTooLongError();
    } else {
      isErrored && setIsErrored(undefined);
      const remainingChars = COLLECTION_NAME_MAX_LENGTH - value.length;
      return Messages.remainingCharacters({
        COUNT: remainingChars,
        COUNT_LOC: Formatters.formatCount(remainingChars)
      });
    }
  };

  React.useEffect(() => {
    inputEl.current?.focus();
  }, []);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.substring(0, COLLECTION_NAME_MAX_LENGTH);
    props.errorMessage && props.clearErrorMessage();
    props.editLogic.setEditorStateWithinEditMode(newText);
    setMessage(getMessageForNameTextInput(newText));
  };

  const [message, setMessage] = React.useState(props.errorMessage || getMessageForNameTextInput(props.editLogic.editorState));

  return <span data-tb-test-id={'collection-name-edit'} className={SpaceHeaderStyles.EditableTitleEditContainer}>
    <TextFieldWidget
      testId='collection-name-edit'
      label=''
      aria-label={Messages.collectionNameTextFieldLabel()}
      maxLength={COLLECTION_NAME_MAX_LENGTH}
      onChange={onNameChange}
      value={props.editLogic.editorState}
      className={`${SpaceHeaderStyles.ColumnFlexGrow} ${SpaceHeaderStyles.EditableTitleInput}`}
      style={SpaceHeaderStyles.EditableTitleInputOverrides}
      message={props.errorMessage || message}
      valid={(!!props.errorMessage || isErrored) ? false : undefined} // valid=undefined so textfield won't have green valid styling
      ref={inputEl}
    />
    <div className={SpaceHeaderStyles.ConfirmCancelButtons}>
      <InlineEditorCancelButton {...props.editLogic} />
      <InlineEditorSaveButton {...props.editLogic} />
    </div>
  </span>;
}
